import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { alertDialog } from '../components/ui/dialog';
import { FeatureManager } from '../../core/feature-manager.js';
import { DEFAULT_ROLE, normalizeRole } from '../../core/roles.esm.js';
import { WindowControlFeature } from '../../features/window-control/index.js';
import { ChatFeature } from '../../features/chat/index.js';
import { KeystrokeDisplayFeature } from '../../features/keystroke-display/index.js';
import { ConfigManagerFeature } from '../../features/config-manager/index.js';
import { TimelineFeature } from '../../features/timeline/index.js';
import { ScreenshotFeature } from '../../features/screenshot/index.js';
import { FileBlockFeature } from '../../features/file-block/index.js';
import { FileBrowserFeature } from '../../features/file-browser/index.js';
import { TimeLimitFeature } from '../../features/time-limit/index.js';
import { AIVoiceFeature } from '../../features/ai-voice/index.js';
import { ChallengesFeature } from '../../features/challenges/index.js';
import { CorruptionFeature } from '../../features/corruption/index.js';
import { InterrogationFeature } from '../../features/interrogation/index.js';
import { EconomyFeature } from '../../features/economy/index.js';
import { TaskBoardFeature } from '../../features/task-board/index.js';
import { JournalFeature } from '../../features/journal/index.js';
import { GazeTrackingFeature } from '../../features/gaze-tracking/index.js';
import { InputSabotageFeature } from '../../features/input-sabotage/index.js';
import { BrowserExtensionFeature } from '../../features/browser-extension/index.js';
import { SiegeFeature } from '../../features/siege/index.js';
import { KeywordDatabaseFeature } from '../../features/keyword-database/index.js';
import { PiShockFeature } from '../../features/pishock/index.js';
import { InstalledAppsFeature } from '../../features/installed-apps/index.js';
import { AIConfigFeature } from '../../features/ai-config/index.js';
import { SpinWheelFeature } from '../../features/spin-wheel/index.js';
import { WriteLinesFeature } from '../../features/write-lines/index.js';
import { DossierFeature } from '../../features/dossier/index.js';
import { CameraMonitorFeature } from '../../features/camera-monitor/index.js';
import { WebcamSamplerFeature } from '../../features/webcam-sampler/index.js';
import { SoundBlasterFeature } from '../../features/sound-blaster/index.js';
import { SacredTimelineFeature } from '../../features/sacred-timeline/index.js';
import { CollarComplianceFeature } from '../../features/collar-compliance/index.js';
import { ScreenRecorderFeature } from '../../features/screen-recorder/index.js';
import { WindowRearrangeFeature } from '../../features/window-rearrange/index.js';
import { CountdownTimerFeature } from '../../features/countdown-timer/index.js';
import { ConsentFeature } from '../../features/consent/index.js';
import { MercyFeature } from '../../features/mercy/index.js';
import { LimitsManifestFeature } from '../../features/limits-manifest/index.js';
import { ReleaseFeature } from '../../features/release/index.js';
import { DebugWindowFeature } from '../../features/debug-window/index.js';
import { QuickConfessionFeature } from '../../features/quick-confession/index.js';
import { PhantomCursorFeature } from '../../features/phantom-cursor/index.js';
import { DigitalGraffitiFeature } from '../../features/digital-graffiti/index.js';
import { WallpaperHijackFeature } from '../../features/wallpaper-hijack/index.js';
import { QuizFeature } from '../../features/quiz/index.js';
import { WordSearchFeature } from '../../features/word-search/index.js';
import { EyeLockFeature } from '../../features/eye-lock/index.js';
import { CardWarFeature } from '../../features/card-war/index.js';
import { LabyrinthFeature } from '../../features/labyrinth/index.js';
import { UserPanelFeature } from '../../features/user-panel/index.js';
import { ScreenTintFeature } from '../../features/screen-tint/index.js';
import { TabSurveillanceFeature } from '../../features/tab-surveillance/index.js';
// Game features — these were missing prior to this fix, which is why
// Start Game silently no-op'd from the website. featureManager.getFeature('chess')
// returned null because ChessFeature was never registered.
import { BattleshipFeature } from '../../features/battleship/index.js';
import { BlackjackFeature } from '../../features/blackjack/index.js';
import { ChessFeature } from '../../features/chess/index.js';
import { DungeonCrawlerFeature } from '../../features/dungeon-crawler/index.js';
import { ForcedBrowserFeature } from '../../features/forced-browser/index.js';
import { GoFishFeature } from '../../features/go-fish/index.js';
import { HangmanFeature } from '../../features/hangman/index.js';
import { MemoryMatchFeature } from '../../features/memory-match/index.js';
import { JigsawFeature } from '../../features/jigsaw/index.js';
import { getAccessToken, getEnvironmentWsUrl } from '../services/auth.js';
import { coordinateMapper } from '../utils/CoordinateMapper.js';
import { createTimestampThrottle } from '../utils/timestamp-throttle.js';

const WebRTCContext = createContext(null);
// Exported so tests can wrap components in a stub provider (see test/render-with-providers.jsx).
export { WebRTCContext };

// Upper bound on the outgoing message queue used while no DataChannel is open.
// The queue lets on-mount feature queries survive a cold load / reconnect (they
// flush when the channel opens); this cap keeps a prolonged disconnect from
// growing it without limit. On-mount queries sit near the tail, so they survive
// trimming — only ancient overflow is dropped.
const MAX_QUEUED_MESSAGES = 200;

/**
 * Decide how an outgoing DataChannel message should be handled given the current
 * channel state. Pure (no refs/DOM) so the queue-vs-send policy is unit-testable.
 *
 * The critical rule this encodes: when a message can't be delivered right now
 * (no open channel), the action is 'queue' — NEVER drop. An earlier version
 * dropped feature messages here, which lost a view's on-mount query on a cold
 * load / hard refresh; the desktop never replied, so the view's "loaded" event
 * never fired and the page hung on its loading skeleton forever. The queue is
 * flushed (honouring targetId) the instant a channel opens.
 *
 * @param {Object} p
 * @param {boolean} p.hasTargetId          - message is addressed to a specific peer
 * @param {boolean} p.hasOpenTargetChannel - that peer's DataChannel is open
 * @param {boolean} p.hasAnyOpenChannel    - at least one DataChannel is open
 * @returns {{action: 'send-target'|'broadcast'|'queue'}}
 */
export function planOutgoingDelivery({ hasTargetId, hasOpenTargetChannel, hasAnyOpenChannel }) {
  if (hasTargetId) {
    return { action: hasOpenTargetChannel ? 'send-target' : 'queue' };
  }
  return { action: hasAnyOpenChannel ? 'broadcast' : 'queue' };
}

export const useWebRTC = () => {
  const context = useContext(WebRTCContext);
  if (!context) {
    throw new Error('useWebRTC must be used within a WebRTCProvider');
  }
  return context;
};

export const WebRTCProvider = ({ children }) => {
  const [wsUrl, setWsUrl] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [status, setStatus] = useState('Disconnected');
  const [peers, setPeers] = useState(new Map());
  const [connectedPeers, setConnectedPeers] = useState([]); // Track peers with active data channels
  const [remoteCapabilities, setRemoteCapabilities] = useState(null); // Feature capabilities from desktop

  // Persistent monitor identity (replaces viewerId)
  // Monitors are website viewers that generate pairing codes
  const [monitorId] = useState(() => {
    // Load or generate monitor ID
    let id = localStorage.getItem('monitorId');
    if (!id) {
      // Migrate from old viewerId if it exists
      const oldId = localStorage.getItem('viewerId');
      if (oldId) {
        id = oldId.replace('viewer-', 'monitor-');
        console.log('[WebRTC] Migrated viewerId to monitorId:', id);
      } else {
        id = 'monitor-' + crypto.randomUUID().substring(0, 8);
        console.log('[WebRTC] Generated new monitor ID:', id);
      }
      localStorage.setItem('monitorId', id);
    }
    return id;
  });

  // Monitor display name (user-configurable)
  const [monitorName, setMonitorName] = useState(() => {
    return localStorage.getItem('monitorName') || '';
  });

  // Generated pairing code state
  const [generatedCode, setGeneratedCode] = useState(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState(null);
  const [codeExpiresIn, setCodeExpiresIn] = useState(null);
  // Last pairing-code failure (e.g. not authenticated). Surfaced to the operator
  // instead of being silently swallowed, which reads as "nothing happens".
  const [pairingError, setPairingError] = useState(null);

  const [boundSessions, setBoundSessions] = useState([]); // Desktop sessions this monitor is bound to
  const boundSessionsRef = useRef([]); // Ref for synchronous access in message handlers
  const [pairingStatus, setPairingStatus] = useState(null); // Current pairing attempt status

  // Keep viewerId as an alias for backward compatibility
  const viewerId = monitorId;

  const wsRef = useRef(null);
  const peersRef = useRef(new Map());
  const peerDataChannelsRef = useRef(new Map());
  const pendingIceCandidatesRef = useRef(new Map()); // Queue ICE candidates until remote description is set
  const iceCandidateBatchesRef = useRef(new Map()); // ICE candidate batching: peerId -> { candidates: [], timeout: null }
  const featureManagerRef = useRef(null);

  // Store video tracks so they can be re-attached when components mount/unmount
  // Map: peerId -> { camera: MediaStreamTrack, screenShare: MediaStreamTrack }
  const videoTracksRef = useRef(new Map());

  // Connection resilience state
  const [lastDataReceived, setLastDataReceived] = useState(null);
  // Exact wall-clock time of the last DataChannel message (updated on every
  // message, cheap, no render). `lastDataReceived` STATE is flushed from this at
  // most ~once/second so that a busy DataChannel (heartbeats, binary frames,
  // telemetry) doesn't re-render every useWebRTC() consumer on every message.
  const lastDataReceivedRef = useRef(null);
  const dataReceivedFlushGateRef = useRef(null);
  if (!dataReceivedFlushGateRef.current) {
    dataReceivedFlushGateRef.current = createTimestampThrottle(1000);
  }
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  // Reconnection logic
  const intentionalDisconnectRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000;
  const wsKeepaliveRef = useRef(null);

  // Track if we've joined the room (prevents race condition with bound_sessions)
  const hasJoinedRef = useRef(false);

  // DataChannel heartbeat to detect dead peers
  const heartbeatTimersRef = useRef(new Map()); // peerId -> { interval, state }
  const HEARTBEAT_INTERVAL_MS = 10000; // Send ping every 10s
  const HEARTBEAT_TIMEOUT_MS = 30000;  // Dead if no pong in 30s

  // Connection quality stats derived from heartbeat ping/pong RTT
  const [connectionStats, setConnectionStats] = useState({
    latency: null,       // RTT in ms (null = unknown)
    quality: 'unknown',  // 'good' | 'fair' | 'poor' | 'disconnected' | 'unknown'
    lastPingAt: null,     // Timestamp of last ping sent
  });

  // Periodic re-join timer: after joining, if no peers connect within a few seconds,
  // re-send 'join' so the backend sends fresh peer-joined notifications to desktops.
  // This handles the case where the backend disconnected and the desktop reconnected
  // before us — the desktop's initial join didn't find us, so it never sent an offer.
  const rejoinTimerRef = useRef(null);
  const rejoinAttemptsRef = useRef(0);
  const MAX_REJOIN_ATTEMPTS = 4;
  const REJOIN_INTERVAL_MS = 5000;

  // Connection debug info: per-peer negotiation progress
  // Map: peerId -> { offerReceived, answerSent, iceState, connectionState, dcState, timestamps }
  const peerNegotiationRef = useRef(new Map());
  const [peerNegotiationInfo, setPeerNegotiationInfo] = useState(new Map());

  const updatePeerNegotiation = (peerId, updates) => {
    const current = peerNegotiationRef.current.get(peerId) || {
      offerReceived: false,
      answerSent: false,
      iceConnectionState: 'new',
      connectionState: 'new',
      iceGatheringState: 'new',
      dataChannelState: 'none',
      timestamps: {},
    };
    const updated = {
      ...current,
      ...updates,
      timestamps: { ...current.timestamps, ...updates.timestamps },
    };
    peerNegotiationRef.current.set(peerId, updated);
    setPeerNegotiationInfo(new Map(peerNegotiationRef.current));
  };

  // Authentication error state (e.g., token expired, unauthorized)
  const [authError, setAuthError] = useState(null);

  // Chunking support for large DataChannel messages
  const MAX_CHUNK_SIZE = 200 * 1024; // 200KB per chunk
  const pendingChunksRef = useRef(new Map()); // chunkId -> { chunks: [], totalChunks, receivedCount }

  // Binary message callbacks for video streaming
  // Map: streamIdHash -> callback(chunkData, chunkIndex, isLast)
  const binaryCallbacksRef = useRef(new Map());

  // Message queue for when DataChannel is not available
  // Messages are queued instead of falling back to WebSocket
  const [messageQueue, setMessageQueue] = useState([]);
  const messageQueueRef = useRef([]); // Ref for synchronous access

  // Current WebSocket session ID (generated on connect, cleared on disconnect)
  const [sessionId, setSessionId] = useState(null);

  // Selected peer ID getter - registered by SocketContext
  // This allows features to get the currently selected peer without stale closures
  const selectedPeerIdGetterRef = useRef(null);

  /**
   * Register a function to get the currently selected peer ID
   * Called by SocketContext to provide access to selectedPeerId
   */
  const registerSelectedPeerIdGetter = useCallback((getter) => {
    selectedPeerIdGetterRef.current = getter;
  }, []);

  /**
   * Get the currently selected peer ID
   * Returns null if no peer is selected or getter not registered
   */
  const getSelectedPeerId = useCallback(() => {
    return selectedPeerIdGetterRef.current ? selectedPeerIdGetterRef.current() : null;
  }, []);

  /**
   * Check if a peer has an active DataChannel by directly inspecting the ref
   * This is more reliable than checking connectedPeers state which can get out of sync
   * @param {string} peerId - The peer's connection ID
   * @returns {boolean} true if the peer has an open DataChannel
   */
  const hasActiveDataChannel = useCallback((peerId) => {
    if (!peerId) return false;
    const channel = peerDataChannelsRef.current.get(peerId);
    return channel && channel.readyState === 'open';
  }, []);

  /**
   * Handle incoming chunk, reassemble when complete
   * @returns {Object|null} Reassembled message or null if still waiting for chunks
   */
  const handleIncomingChunk = (chunk) => {
    const { chunkId, chunkIndex, totalChunks, data } = chunk;

    if (!pendingChunksRef.current.has(chunkId)) {
      pendingChunksRef.current.set(chunkId, {
        chunks: new Array(totalChunks),
        totalChunks,
        receivedCount: 0,
        timestamp: Date.now()
      });
    }

    const pending = pendingChunksRef.current.get(chunkId);

    if (!pending.chunks[chunkIndex]) {
      pending.chunks[chunkIndex] = data;
      pending.receivedCount++;
    }

    if (pending.receivedCount === pending.totalChunks) {
      // All chunks received, reassemble
      const fullMessage = pending.chunks.join('');
      pendingChunksRef.current.delete(chunkId);

      console.log(`[WebRTC] Reassembled chunked message (${fullMessage.length} bytes from ${totalChunks} chunks)`);

      try {
        return JSON.parse(fullMessage);
      } catch (error) {
        console.error('[WebRTC] Error parsing reassembled message:', error);
        return null;
      }
    }

    return null; // Still waiting for more chunks
  };

  /**
   * Send a message via DataChannel, chunking if necessary
   */
  const sendViaDataChannel = (channel, message, peerId) => {
    const messageStr = JSON.stringify(message);

    if (messageStr.length <= MAX_CHUNK_SIZE) {
      // Small enough to send directly
      channel.send(messageStr);
    } else {
      // Need to chunk the message
      const chunkId = Math.random().toString(36).substring(2, 15);
      const chunks = [];

      for (let i = 0; i < messageStr.length; i += MAX_CHUNK_SIZE) {
        chunks.push(messageStr.slice(i, i + MAX_CHUNK_SIZE));
      }

      console.log(`[WebRTC] Chunking message (${messageStr.length} bytes) into ${chunks.length} chunks`);

      chunks.forEach((chunkData, index) => {
        const chunk = {
          type: 'chunk',
          chunkId,
          chunkIndex: index,
          totalChunks: chunks.length,
          data: chunkData
        };
        channel.send(JSON.stringify(chunk));
      });
    }
  };

  // Initialize feature manager
  useEffect(() => {
    const getCoreAPI = () => ({
      sendFeatureMessage: (message) => sendFeatureMessage(message),
      broadcastDataChannelMessage: (message) => broadcastDataChannelMessage(message),
      getFeatureManager: () => featureManagerRef.current,
      getSelectedPeerId: () => getSelectedPeerId(), // Get current selected peer (from SocketContext)
      connectionId: 'viewer-' + Math.random().toString(36).substring(2, 6)
    });

    featureManagerRef.current = new FeatureManager(getCoreAPI());

    // Dev-only introspection hook for the DevBridge.
    if (import.meta.env.DEV) {
      window.__INQUISITOR_DEV = window.__INQUISITOR_DEV || {};
      window.__INQUISITOR_DEV.featureManager = featureManagerRef.current;
      window.__INQUISITOR_DEV.getCoreAPI = getCoreAPI;
      // Refs needed to debug "messages aren't reaching the desktop" cases — the
      // dcMap is closure-private to this provider, so without this hook there's
      // no way to inspect channel state from devbridge.
      window.__INQUISITOR_DEV.getDataChannels = () => peerDataChannelsRef.current;
      window.__INQUISITOR_DEV.getPeers = () => peersRef.current;
    }

    // Register features and then auto-connect
    const initAndConnect = async () => {
      console.log('[WebRTC] Starting feature registration...');

      // Register all features - continues even if some fail
      const { succeeded, failed } = await featureManagerRef.current.registerFeatures([
        ConfigManagerFeature,
        WindowControlFeature,
        ChatFeature,
        KeystrokeDisplayFeature,
        TimelineFeature,
        ScreenshotFeature,
        FileBlockFeature,
        FileBrowserFeature,
        TimeLimitFeature,
        AIVoiceFeature,
        ChallengesFeature,
        CorruptionFeature,
        InterrogationFeature,
        EconomyFeature,
        TaskBoardFeature,
        JournalFeature,
        GazeTrackingFeature,
        InputSabotageFeature,
        BrowserExtensionFeature,
        SiegeFeature,
        KeywordDatabaseFeature,
        PiShockFeature,
        InstalledAppsFeature,
        AIConfigFeature,
        SpinWheelFeature,
        WriteLinesFeature,
        DossierFeature,
        CameraMonitorFeature,
        WebcamSamplerFeature,
        SoundBlasterFeature,
        SacredTimelineFeature,
        CollarComplianceFeature,
        ScreenRecorderFeature,
        WindowRearrangeFeature,
        CountdownTimerFeature,
        ConsentFeature,
        MercyFeature,
        LimitsManifestFeature,
        ReleaseFeature,
        DebugWindowFeature,
        QuickConfessionFeature,
        PhantomCursorFeature,
        DigitalGraffitiFeature,
        WallpaperHijackFeature,
        QuizFeature,
        WordSearchFeature,
        EyeLockFeature,
        CardWarFeature,
        LabyrinthFeature,
        ScreenTintFeature,
        TabSurveillanceFeature,
        UserPanelFeature,
        // Game features (see import comment above)
        BattleshipFeature,
        BlackjackFeature,
        ChessFeature,
        DungeonCrawlerFeature,
        ForcedBrowserFeature,
        GoFishFeature,
        HangmanFeature,
        MemoryMatchFeature,
        JigsawFeature
      ]);

      console.log(`[WebRTC] Feature registration complete. Succeeded: ${succeeded.join(', ') || 'none'}`);
      if (failed.length > 0) {
        console.warn(`[WebRTC] Failed to register: ${failed.join(', ')}`);
      }

      // Register screen-capture handler for messages from MediaManager (desktop)
      // These are not a full feature — just screen share lifecycle + foreground window updates
      featureManagerRef.current.messageHandlers.set('screen-capture', async (message) => {
        const { cmd, payload, fromId } = message;
        const peerId = fromId;
        switch (cmd) {
          case 'screen_share_started': {
            const startPeerId = peerId;
            const tracks = startPeerId ? videoTracksRef.current.get(startPeerId) : null;
            console.log(`[WebRTC] Screen share started by peer: ${startPeerId?.substring(0, 8) || 'unknown'}`, {
              hasTracks: !!tracks,
              screenShare: tracks?.screenShare?.readyState || 'none',
              camera: tracks?.camera?.readyState || 'none',
              screenShareActive: tracks?.screenShareActive
            });
            if (startPeerId) {
              if (tracks) {
                tracks.screenShareActive = true;
                const screenTrack = tracks.screenShare || tracks.camera;
                if (screenTrack) {
                  attachVideoToContainer(startPeerId, 'Screen-Share', screenTrack);
                } else {
                  console.warn(`[WebRTC] screen_share_started but no track available for ${startPeerId.substring(0, 8)}`);
                }
              } else {
                // Race: screen_share_started arrived before ontrack — store flag for later
                console.warn(`[WebRTC] screen_share_started but no track entry yet for ${startPeerId.substring(0, 8)}, creating with screenShareActive=true`);
                videoTracksRef.current.set(startPeerId, { camera: null, screenShare: null, screenShareActive: true });
              }
            }
            break;
          }
          case 'screen_share_stopped': {
            const stopPeerId = peerId;
            console.log(`[WebRTC] Screen share stopped by peer: ${stopPeerId?.substring(0, 8) || 'unknown'}`);
            if (stopPeerId) {
              const tracks = videoTracksRef.current.get(stopPeerId);
              if (tracks) {
                tracks.screenShareActive = false;
              }
              const videoId = `video-${stopPeerId}-Screen-Share`;
              const videoWrapper = document.getElementById(videoId);
              if (videoWrapper) {
                videoWrapper.remove();
                console.log(`[WebRTC] Removed screen share video element for ${stopPeerId.substring(0, 8)}`);
              }
            }
            break;
          }
          case 'foreground_window_update': {
            // Forward to window-control feature
            const windowControlFeature = featureManagerRef.current.getFeature('window-control');
            if (windowControlFeature) {
              await windowControlFeature.handleMessage({
                action: 'foreground_window_update',
                payload: payload,
                fromId: peerId
              });
            }
            break;
          }
          default:
            console.warn(`[WebRTC] Unknown screen-capture action: ${cmd}`);
        }
      });

      // Get WebSocket URL from environment config (environment takes precedence)
      const envUrl = getEnvironmentWsUrl();
      const urlToUse = envUrl;

      if (urlToUse) {
        setWsUrl(urlToUse);
        // Small delay to ensure React state is updated
        setTimeout(() => {
          if (urlToUse && !wsRef.current) {
            console.log('[WebRTC] Auto-connecting to URL:', urlToUse);
            connectToUrl(urlToUse);
          }
        }, 100);
      } else {
        console.log('[WebRTC] No WebSocket URL configured');
      }
    };

    initAndConnect();

    return () => {
      // Mark as intentional to prevent reconnection during unmount
      intentionalDisconnectRef.current = true;
      cancelReconnect();
      disconnect();
    };
  }, []);

  // Stable identity: consumers pass this to child components and use it as a
  // useEffect dependency, so it must not change every render. Empty deps are
  // safe because its only closure over component scope is `sendMessage`, which
  // is itself a stable useCallback that reads refs (never reactive state).
  const sendFeatureMessage = useCallback((message) => {
    const { targetPeerId, ...messageData } = message;
    const featureMessage = {
      type: 'feature_message',
      ...messageData,
      messageId: message.messageId || Math.random().toString(36).substring(2, 15),
      timestamp: Date.now()
    };

    if (targetPeerId) {
      featureMessage.targetId = targetPeerId;
    }

    sendMessage(featureMessage);
    return featureMessage.messageId;
  }, []);

  const broadcastDataChannelMessage = (message) => {
    peerDataChannelsRef.current.forEach((channel, peerId) => {
      if (channel.readyState === 'open') {
        try {
          channel.send(JSON.stringify(message));
        } catch (error) {
          console.error(`Error broadcasting to ${peerId}:`, error);
        }
      }
    });
  };

  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  const scheduleReconnect = (url) => {
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('[WebRTC] Max reconnection attempts reached, giving up');
      setStatus('Connection lost - click Connect to retry');
      return;
    }

    // Calculate delay with exponential backoff (1s, 2s, 4s, 8s... capped at 30s)
    const delay = Math.min(
      baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current),
      30000
    );

    reconnectAttemptsRef.current++;
    setReconnectAttempt(reconnectAttemptsRef.current);
    console.log(`[WebRTC] Scheduling reconnection attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts} in ${delay}ms`);
    setStatus(`Reconnecting (${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`);

    reconnectTimerRef.current = setTimeout(() => {
      if (intentionalDisconnectRef.current) {
        console.log('[WebRTC] Reconnection cancelled - intentional disconnect');
        return;
      }

      console.log(`[WebRTC] Attempting reconnection ${reconnectAttemptsRef.current}/${maxReconnectAttempts}...`);
      connectToUrl(url);
    }, delay);
  };

  /**
   * Cancel any pending reconnection attempt
   */
  const cancelReconnect = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
    setReconnectAttempt(0);
  };

  /**
   * Start periodic re-join after initial join.
   * If no peer connections exist after REJOIN_INTERVAL_MS, re-send 'join' so the
   * backend sends fresh peer-joined notifications to any desktops that reconnected
   * before us and didn't know we were here.
   */
  const startRejoinTimer = () => {
    stopRejoinTimer();
    rejoinAttemptsRef.current = 0;

    rejoinTimerRef.current = setInterval(() => {
      // Stop if we've exceeded max attempts
      if (rejoinAttemptsRef.current >= MAX_REJOIN_ATTEMPTS) {
        console.log('[WebRTC] Max rejoin attempts reached, stopping periodic rejoin');
        stopRejoinTimer();
        return;
      }

      // Stop if we already have peer connections (offer was received)
      if (peersRef.current.size > 0) {
        console.log('[WebRTC] Peer connections exist, stopping periodic rejoin');
        stopRejoinTimer();
        return;
      }

      // Stop if WebSocket is gone
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        stopRejoinTimer();
        return;
      }

      rejoinAttemptsRef.current++;
      console.log(`[WebRTC] No peers yet — re-sending join (attempt ${rejoinAttemptsRef.current}/${MAX_REJOIN_ATTEMPTS})`);
      sendMessage({ type: 'join' });
    }, REJOIN_INTERVAL_MS);
  };

  const stopRejoinTimer = () => {
    if (rejoinTimerRef.current) {
      clearInterval(rejoinTimerRef.current);
      rejoinTimerRef.current = null;
    }
    rejoinAttemptsRef.current = 0;
  };

  /**
   * WebSocket keepalive to prevent API Gateway 10-minute idle timeout
   */
  const startWsKeepalive = () => {
    stopWsKeepalive();
    wsKeepaliveRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping', action: 'sendMessage' }));
      }
    }, 5 * 60 * 1000); // Every 5 minutes
  };

  const stopWsKeepalive = () => {
    if (wsKeepaliveRef.current) {
      clearInterval(wsKeepaliveRef.current);
      wsKeepaliveRef.current = null;
    }
  };

  /**
   * Start DataChannel heartbeat for a peer to detect dead connections
   */
  const startHeartbeat = (peerId, dataChannel) => {
    stopHeartbeat(peerId);

    const state = { lastPongAt: Date.now() };

    const interval = setInterval(() => {
      if (dataChannel.readyState !== 'open') {
        stopHeartbeat(peerId);
        return;
      }

      const elapsed = Date.now() - state.lastPongAt;
      if (elapsed > HEARTBEAT_TIMEOUT_MS) {
        console.warn(`[Heartbeat] No pong from ${peerId.substring(0, 8)} for ${Math.round(elapsed / 1000)}s — removing dead peer`);
        setConnectionStats({ latency: null, quality: 'disconnected', lastPingAt: null });
        stopHeartbeat(peerId);
        const peerConnection = peersRef.current.get(peerId);
        if (peerConnection) peerConnection.close();
        peersRef.current.delete(peerId);
        peerDataChannelsRef.current.delete(peerId);
        pendingIceCandidatesRef.current.delete(peerId);
        videoTracksRef.current.delete(peerId);
        peerIdentitiesRef.current.delete(peerId);
        setPeers(new Map(peersRef.current));
        updateConnectedPeersList();
        // Re-join so the desktop discovers us
        if (peersRef.current.size === 0 && !intentionalDisconnectRef.current) {
          startRejoinTimer();
        }
        return;
      }

      try {
        dataChannel.send(JSON.stringify({ type: 'dc_ping', ts: Date.now() }));
      } catch (error) {
        console.error(`[Heartbeat] Error sending ping to ${peerId.substring(0, 8)}:`, error);
      }
    }, HEARTBEAT_INTERVAL_MS);

    heartbeatTimersRef.current.set(peerId, { interval, state });
    console.log(`[Heartbeat] Started heartbeat for ${peerId.substring(0, 8)}`);
  };

  const stopHeartbeat = (peerId) => {
    const entry = heartbeatTimersRef.current.get(peerId);
    if (entry) {
      clearInterval(entry.interval);
      heartbeatTimersRef.current.delete(peerId);
    }
  };

  const handlePong = (peerId, pongTs) => {
    const entry = heartbeatTimersRef.current.get(peerId);
    if (entry) {
      entry.state.lastPongAt = Date.now();

      // Calculate RTT from the original ping timestamp echoed back in pong
      if (pongTs) {
        const rtt = Date.now() - pongTs;
        const quality = rtt < 100 ? 'good' : rtt < 300 ? 'fair' : 'poor';
        setConnectionStats({
          latency: rtt,
          quality,
          lastPingAt: Date.now(),
        });
      }
    }
  };

  const stopAllHeartbeats = () => {
    heartbeatTimersRef.current.forEach(entry => clearInterval(entry.interval));
    heartbeatTimersRef.current.clear();
    setConnectionStats({ latency: null, quality: 'unknown', lastPingAt: null });
  };

  /**
   * Queue a message for later delivery when DataChannel becomes available
   */
  const queueMessage = (message) => {
    const queuedMessage = {
      ...message,
      queuedAt: Date.now(),
      id: Math.random().toString(36).substring(2, 9)
    };
    let next = [...messageQueueRef.current, queuedMessage];
    // Bound the queue: a prolonged disconnect (or a high-frequency sender that
    // doesn't gate on connection state) must not grow it without limit. Keep the
    // most recent MAX_QUEUED_MESSAGES and drop the oldest — an on-mount query is
    // near the tail, so it survives; only ancient overflow is discarded.
    if (next.length > MAX_QUEUED_MESSAGES) {
      next = next.slice(next.length - MAX_QUEUED_MESSAGES);
    }
    messageQueueRef.current = next;
    setMessageQueue(next);
    console.log(`[WebRTC] Queued message: ${message.type}${message.feature ? '.' + message.cmd : ''} (queue size: ${next.length})`);
  };

  /**
   * Flush all queued messages through available DataChannels
   */
  const flushMessageQueue = () => {
    if (messageQueueRef.current.length === 0) return;

    const openChannels = Array.from(peerDataChannelsRef.current.entries())
      .filter(([_, channel]) => channel.readyState === 'open');

    if (openChannels.length === 0) {
      console.log('[WebRTC] Cannot flush queue - no open DataChannels');
      return;
    }

    console.log(`[WebRTC] Flushing ${messageQueueRef.current.length} queued message(s) to ${openChannels.length} peer(s)`);

    const messagesToSend = [...messageQueueRef.current];
    messageQueueRef.current = [];
    setMessageQueue([]);

    messagesToSend.forEach(queuedMessage => {
      // Remove queue metadata before sending
      const { queuedAt, id, ...message } = queuedMessage;

      if (message.targetId) {
        // Targeted message
        const dataChannel = peerDataChannelsRef.current.get(message.targetId);
        if (dataChannel && dataChannel.readyState === 'open') {
          sendViaDataChannel(dataChannel, message, message.targetId);
        }
      } else {
        // Broadcast to all
        openChannels.forEach(([peerId, channel]) => {
          try {
            sendViaDataChannel(channel, message, peerId);
          } catch (error) {
            console.error(`[WebRTC] Error sending queued message to ${peerId.substring(0, 8)}:`, error);
          }
        });
      }
    });
  };

  // Stable identity (empty deps): the whole closure graph it touches —
  // sendViaDataChannel, queueMessage, wsRef/peerDataChannelsRef/messageQueueRef,
  // setMessageQueue — reads only refs, constants, and state setters, never
  // reactive state, so a once-captured closure stays correct for the lifetime
  // of the provider. Keeping it stable lets sendFeatureMessage stay stable too.
  const sendMessage = useCallback((message) => {
    // Message types that MUST go via WebSocket (signaling + backend-only queries)
    const signalingTypes = ['offer', 'answer', 'ice-candidate', 'ice-candidates', 'join', 'leave'];
    const backendOnlyTypes = [
      'register_monitor',
      'get_bound_sessions',
      'create_pairing_code',
      'use_pairing_code',
      'unbind_session',
      'validate_connections'
    ];

    // Always use WebSocket for signaling and backend-only messages
    if (signalingTypes.includes(message.type) || backendOnlyTypes.includes(message.type)) {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.log(`[WebRTC] Sending via WebSocket: ${message.type}`);
        wsRef.current.send(JSON.stringify({ ...message, action: 'sendMessage' }));
      } else {
        console.error(`[WebRTC] Cannot send ${message.type}: WebSocket not available (ref: ${!!wsRef.current}, state: ${wsRef.current?.readyState})`);
      }
      return;
    }

    // Application messages: DataChannel ONLY (no WebSocket fallback). The
    // send-vs-queue decision is a pure function (planOutgoingDelivery) so it can
    // be unit-tested — the regression it guards is real: dropping an undeliverable
    // feature message loses a view's on-mount query on a cold load, and the page
    // then hangs on its loading skeleton forever.
    const targetId = message.targetId;
    const targetChannel = targetId ? peerDataChannelsRef.current.get(targetId) : null;
    const hasAnyOpenChannel = Array.from(peerDataChannelsRef.current.values())
      .some((ch) => ch.readyState === 'open');
    const { action } = planOutgoingDelivery({
      hasTargetId: !!targetId,
      hasOpenTargetChannel: !!(targetChannel && targetChannel.readyState === 'open'),
      hasAnyOpenChannel,
    });

    if (action === 'send-target') {
      sendViaDataChannel(targetChannel, message, targetId);
      return;
    }

    if (action === 'broadcast') {
      let sentCount = 0;
      peerDataChannelsRef.current.forEach((channel, peerId) => {
        if (channel.readyState === 'open') {
          try {
            sendViaDataChannel(channel, message, peerId);
            sentCount++;
          } catch (error) {
            console.error(`[WebRTC] Error sending to ${peerId}:`, error);
          }
        }
      });
      if (sentCount > 0) {
        console.log(`[WebRTC] Broadcast ${message.type}${message.feature ? '.' + message.cmd : ''} to ${sentCount} peer(s) via DataChannel`);
        return;
      }
      // Raced to zero open channels between the readyState check and the send —
      // fall through and queue so the message still reaches the peer.
    }

    // action === 'queue' (or a broadcast that raced to no open channels). Retain
    // the message; the queue is flushed (honouring targetId) when a channel opens.
    // Dropping feature messages here is what hung feature pages on a refresh: the
    // on-mount query was lost and never retried.
    console.log(`[WebRTC] No open DataChannel, queueing ${message.type}${message.feature ? '.' + message.cmd : ''}`);
    queueMessage(message);
  }, []);

  /**
   * Queue ICE candidate for batched sending (reduces API Gateway invocations)
   * Batches are flushed after 50ms of inactivity or when reaching 10 candidates
   */
  const queueIceCandidate = (peerId, candidate) => {
    let batch = iceCandidateBatchesRef.current.get(peerId);
    if (!batch) {
      batch = { candidates: [], timeout: null };
      iceCandidateBatchesRef.current.set(peerId, batch);
    }

    batch.candidates.push(candidate);

    // Clear existing timeout and set new one (debounce pattern)
    if (batch.timeout) {
      clearTimeout(batch.timeout);
    }

    // Flush after 50ms of no new candidates, or immediately if we hit 10
    const BATCH_DELAY_MS = 50;
    const MAX_BATCH_SIZE = 10;

    if (batch.candidates.length >= MAX_BATCH_SIZE) {
      flushIceCandidates(peerId);
    } else {
      batch.timeout = setTimeout(() => {
        flushIceCandidates(peerId);
      }, BATCH_DELAY_MS);
    }
  };

  /**
   * Send batched ICE candidates to peer
   */
  const flushIceCandidates = (peerId) => {
    const batch = iceCandidateBatchesRef.current.get(peerId);
    if (!batch || batch.candidates.length === 0) return;

    if (batch.timeout) {
      clearTimeout(batch.timeout);
    }

    console.log(`[WebRTC] Sending ${batch.candidates.length} batched ICE candidates to ${peerId.substring(0, 8)}`);

    sendMessage({
      type: 'ice-candidates', // Plural - batched
      targetId: peerId,
      data: batch.candidates
    });

    iceCandidateBatchesRef.current.delete(peerId);
  };

  const connectToUrl = async (url) => {
    if (!url || !url.trim()) {
      console.error('[WebRTC] Cannot connect: invalid URL');
      return;
    }

    // Close existing connection if any
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('[WebRTC] Closing existing connection before reconnecting');
      wsRef.current.close();
    }

    try {
      // Get access token for authentication
      let wsUrl = url;
      try {
        const token = await getAccessToken();
        const urlObj = new URL(url);
        // Always identify as monitor (website client)
        urlObj.searchParams.set('type', 'monitor');
        if (token) {
          // Append token as query parameter
          urlObj.searchParams.set('token', token);
          console.log('[WebRTC] Connecting with authentication token');
        } else {
          console.warn('[WebRTC] No auth token available, connecting without authentication');
        }
        wsUrl = urlObj.toString();
      } catch (authError) {
        console.error('[WebRTC] Failed to get auth token:', authError);
        // Continue without token - authorizer will reject if required
      }

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WebRTC] WebSocket connected');
        setIsConnected(true);
        setStatus('Connected - Viewer Mode');

        // Generate and store session ID for this connection
        const newSessionId = Math.random().toString(36).substring(2, 10);
        setSessionId(newSessionId);
        console.log('[WebRTC] Session ID:', newSessionId);

        // Reset reconnection state on successful connection
        intentionalDisconnectRef.current = false;
        reconnectAttemptsRef.current = 0;
        setReconnectAttempt(0);

        // Clean up stale peer connections from previous WS session.
        // On reconnect we get a new connectionId, so the desktop will create
        // new peer connections with different peerIds. Old ones are orphaned.
        if (peersRef.current.size > 0) {
          console.log(`[WebRTC] WS reconnected - cleaning up ${peersRef.current.size} stale peer connection(s)`);
          peersRef.current.forEach((peer) => peer.close());
          peersRef.current.clear();
          peerDataChannelsRef.current.clear();
          pendingIceCandidatesRef.current.clear();
          videoTracksRef.current.clear();
          peerIdentitiesRef.current.clear();
          binaryCallbacksRef.current.clear();
          messageQueueRef.current = [];
          setPeers(new Map());
          setConnectedPeers([]);
          setMessageQueue([]);
        }

        // Start WebSocket keepalive to prevent API Gateway idle timeout
        startWsKeepalive();

        // Enable chat feature
        const chatFeature = featureManagerRef.current.getFeature('chat');
        if (chatFeature) {
          chatFeature.enable();
        }

        // IMPORTANT: Register monitor identity BEFORE joining
        // The join handler checks identity to determine which peers to notify.
        // If we join before registering, the backend skips peer notifications
        // because it doesn't know who we are yet (race condition fix).
        sendMessage({ type: 'register_monitor', monitorId, name: monitorName || 'Web Monitor' });

        // Fetch bound sessions (desktops this monitor has paired with)
        // NOTE: We delay sending 'join' until bound_sessions response arrives.
        // This prevents a race condition where desktop offers arrive before
        // we know which sessions are authorized, causing video to be rejected.
        hasJoinedRef.current = false;
        sendMessage({ type: 'get_bound_sessions', viewerId: monitorId });
      };

      ws.onmessage = (event) => {
        handleMessage(JSON.parse(event.data)).catch(error => {
          console.error('[WebRTC] Unhandled error in message handler:', error);
        });
      };

      ws.onerror = (error) => {
        console.error('[WebRTC] WebSocket error:', error);
        setStatus('Connection error');
        // Note: WebSocket doesn't expose HTTP status codes in onerror
        // Auth failures are detected via onclose with code 4401/4403
      };

      ws.onclose = (event) => {
        console.log(`[WebRTC] WebSocket closed (code: ${event.code}, reason: ${event.reason || 'none'})`);
        setIsConnected(false);
        setSessionId(null);
        stopWsKeepalive();
        stopRejoinTimer();

        // Check for authentication failure (API Gateway returns 401/403 as close)
        // Code 1006 (abnormal closure) often indicates auth rejection before connect
        // Code 4401/4403 are custom codes we might use
        if (event.code === 1006 || event.code === 4401 || event.code === 4403) {
          const wasAuthError = !event.wasClean && reconnectAttemptsRef.current === 0;
          if (wasAuthError) {
            console.error('[WebRTC] Connection rejected - possible authentication failure');
            setStatus('Authentication failed - please log in again');
            setAuthError('Connection rejected. Your session may have expired.');
            // Don't auto-reconnect on auth errors
            intentionalDisconnectRef.current = true;
            cleanup();
            return;
          }
        }

        // Only tear down peer connections on intentional disconnect.
        // WebRTC peer connections are P2P and survive signaling loss.
        // On unexpected disconnect, keep peers alive while we reconnect.
        if (intentionalDisconnectRef.current) {
          cleanup();
        } else {
          setStatus('Reconnecting...');
          scheduleReconnect(url);
        }
      };
    } catch (error) {
      console.error('[WebRTC] Error connecting:', error);
      setStatus('Connection failed: ' + error.message);
    }
  };

  const connect = async () => {
    if (!wsUrl.trim()) {
      await alertDialog({ title: 'Missing URL', message: 'Please enter a WebSocket URL', variant: 'warning' });
      return;
    }
    await connectToUrl(wsUrl);
  };

  const disconnect = () => {
    // Mark as intentional disconnect to prevent auto-reconnect
    intentionalDisconnectRef.current = true;
    cancelReconnect();
    stopWsKeepalive();

    if (wsRef.current) {
      sendMessage({ type: 'leave' });
      wsRef.current.close();
    }
    cleanup();
  };

  // Request the backend to validate all connections and clean up stale ones
  const validateConnections = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('[WebRTC] Manually requesting connection validation...');
      sendMessage({ type: 'validate_connections' });
    } else {
      console.warn('[WebRTC] Cannot validate connections - not connected to signaling server');
    }
  };

  const updateConnectedPeersList = () => {
    const connectedList = Array.from(peerDataChannelsRef.current.entries())
      .filter(([peerId, channel]) => channel.readyState === 'open')
      .map(([peerId, channel]) => ({
        id: peerId,
        shortId: peerId.substring(0, 8),
        connected: true
      }));
    setConnectedPeers(connectedList);
  };

  const cleanup = () => {
    stopAllHeartbeats(); // Stop all DataChannel heartbeats
    peersRef.current.forEach((peer) => peer.close());
    peersRef.current.clear();
    peerDataChannelsRef.current.clear();
    pendingIceCandidatesRef.current.clear(); // Clear pending ICE candidates
    videoTracksRef.current.clear(); // Clear stored video tracks
    peerIdentitiesRef.current.clear(); // Clear peer identity mappings
    binaryCallbacksRef.current.clear(); // Clear binary streaming callbacks
    hasJoinedRef.current = false; // Reset so reconnect can join again
    stopRejoinTimer(); // Stop periodic rejoin
    boundSessionsRef.current = []; // Reset for clean reconnect
    messageQueueRef.current = []; // Clear message queue
    peerNegotiationRef.current.clear();
    setPeerNegotiationInfo(new Map());
    setPeers(new Map());
    setConnectedPeers([]);
    setMessageQueue([]);

    const chatFeature = featureManagerRef.current?.getFeature('chat');
    if (chatFeature) {
      chatFeature.disable();
    }
  };

  // Track peer identities for security validation
  const peerIdentitiesRef = useRef(new Map());

  /**
   * SECURITY: Check if a desktop session is authorized (in boundSessions list)
   * @param {string} sessionId - The desktop's sessionId
   * @returns {boolean} true if authorized
   */
  const isSessionAuthorized = (sessionId) => {
    if (!sessionId) return false;
    // Use ref for synchronous access - React state updates are async
    const sessions = boundSessionsRef.current;
    const isAuthorized = sessions.some(s => s.sessionId === sessionId);
    if (!isAuthorized) {
      console.warn(`[Security] Session ${sessionId.substring(0, 8)} not in bound sessions`);
      console.log(`[Security] Bound sessions:`, sessions.map(s => s.sessionId?.substring(0, 8)));
    }
    return isAuthorized;
  };

  const handleMessage = async (message) => {
    // Log all incoming messages for debugging
    if (!['ice-candidate', 'ice-candidates'].includes(message.type)) {
      console.log(`[WebRTC] Received message: ${message.type}`, message);
    }
    switch (message.type) {
      case 'offer':
        // SECURITY: Backend now validates pairing before routing offers
        // But we add defense-in-depth validation here too
        if (message.identity && message.identityType === 'desktop') {
          const isAuthorized = isSessionAuthorized(message.identity);
          if (!isAuthorized) {
            console.warn(`[Security] Rejecting offer from unauthorized desktop: ${message.fromId?.substring(0, 8)} (session: ${message.identity?.substring(0, 8)})`);
            break;
          }
          // Store identity mapping
          peerIdentitiesRef.current.set(message.fromId, message.identity);

          // Update the bound session's connectionId to match this new peer.
          // The desktop may reconnect to WebSocket (getting a new connectionId) and
          // send a new offer, but session_status may not arrive or may lag behind.
          // Without this update, hasActiveDataChannel(session.connectionId) returns
          // false because the old connectionId doesn't match the new peerId.
          const newPeerId = message.fromId;
          const sessionId = message.identity;
          setBoundSessions(prev => {
            const updated = prev.map(s =>
              s.sessionId === sessionId
                ? { ...s, connectionId: newPeerId }
                : s
            );
            boundSessionsRef.current = updated;
            return updated;
          });

          // Note: We no longer clean up peers with the same identity here.
          // Multiple tabs from the same monitor can connect simultaneously.
          // Stale peers are cleaned up via connectionstatechange (disconnected/failed).
          updateConnectedPeersList();
          setPeers(new Map(peersRef.current));
        }
        updatePeerNegotiation(message.fromId, {
          offerReceived: true,
          timestamps: { offerReceived: Date.now() },
        });
        // Offer received — no need to keep re-joining
        stopRejoinTimer();
        await handleOffer(message.fromId, message.data);
        break;
      case 'answer':
        // Store identity if provided
        if (message.identity) {
          peerIdentitiesRef.current.set(message.fromId, message.identity);
        }
        await handleAnswer(message.fromId, message.data);
        break;
      case 'ice-candidate':
        await handleIceCandidate(message.fromId, message.data);
        break;
      case 'ice-candidates':
        // Batched ICE candidates - process each one
        for (const candidate of message.data) {
          await handleIceCandidate(message.fromId, candidate);
        }
        break;
      case 'feature_message':
        // Feature messages should only arrive via DataChannel, not WebSocket
        console.warn(`[WebRTC] Ignoring feature_message via WebSocket (should use DataChannel): ${message.feature}.${message.cmd}`);
        break;
      // 'chat' messages now use feature_message protocol via DataChannel
      case 'peers':
        // SECURITY: Backend now only sends authorized (paired) peers
        console.log(`[WebRTC] Received ${message.peers?.length || 0} authorized peers`);
        // Store peer identities if provided
        if (message.peerDetails) {
          message.peerDetails.forEach(detail => {
            if (detail.connectionId && detail.identity) {
              peerIdentitiesRef.current.set(detail.connectionId, detail.identity);
            }
          });
        }
        break;
      case 'peer-joined':
        // SECURITY: Backend now only notifies about paired peers
        // The message includes identity info for validation
        console.log(`[WebRTC] Peer joined: ${message.peerId?.substring(0, 8)} (type: ${message.peerType || 'unknown'}, identity: ${message.identity?.substring(0, 8) || 'unknown'})`);
        // Store identity for later validation
        if (message.peerId && message.identity) {
          peerIdentitiesRef.current.set(message.peerId, message.identity);
        }
        // A new peer joined - we'll establish connection when they send an offer
        break;
      case 'peer-left':
        console.log(`[WebRTC] Peer left: ${message.peerId?.substring(0, 8)} (identity: ${message.identity?.substring(0, 8) || 'unknown'})`);
        // Clean up peer connection and data channel for the departed peer
        if (message.peerId) {
          stopHeartbeat(message.peerId);
          const peerConnection = peersRef.current.get(message.peerId);
          if (peerConnection) {
            peerConnection.close();
            peersRef.current.delete(message.peerId);
            setPeers(new Map(peersRef.current));
          }
          peerDataChannelsRef.current.delete(message.peerId);
          pendingIceCandidatesRef.current.delete(message.peerId);
          videoTracksRef.current.delete(message.peerId);
          peerIdentitiesRef.current.delete(message.peerId); // Clean up identity mapping
          updateConnectedPeersList();
          // Re-join so the desktop (if it reconnects) discovers us
          if (peersRef.current.size === 0 && !intentionalDisconnectRef.current) {
            startRejoinTimer();
          }
        }
        break;
      case 'validated_peers':
        // Response from validate_connections - contains list of valid peer IDs
        console.log(`[WebRTC] Validated peers: ${message.peers?.length || 0} valid, ${message.removed || 0} stale removed`);
        // Clean up any local peer state that's no longer valid
        const validPeerSet = new Set(message.peers || []);
        peersRef.current.forEach((_, peerId) => {
          if (!validPeerSet.has(peerId)) {
            console.log(`[WebRTC] Cleaning up stale peer: ${peerId.substring(0, 8)}`);
            const peerConnection = peersRef.current.get(peerId);
            if (peerConnection) peerConnection.close();
            peersRef.current.delete(peerId);
            peerDataChannelsRef.current.delete(peerId);
            pendingIceCandidatesRef.current.delete(peerId);
            videoTracksRef.current.delete(peerId);
          }
        });
        setPeers(new Map(peersRef.current));
        updateConnectedPeersList();
        break;
      case 'ping':
        // Server is validating our connection - no action needed
        console.log(`[WebRTC] Ping from: ${message.fromId?.substring(0, 8)}`);
        break;

      // Session management messages
      case 'bound_sessions':
        // Received list of desktop sessions this viewer is bound to
        console.log(`[WebRTC] Received ${message.sessions?.length || 0} bound sessions`);
        const sessions = message.sessions || [];
        boundSessionsRef.current = sessions; // Update ref immediately for sync access
        setBoundSessions(sessions);

        // NOW we can safely join - we have the authorized sessions list
        // This prevents the race condition where offers arrive before we know who's authorized
        if (!hasJoinedRef.current) {
          hasJoinedRef.current = true;
          sendMessage({ type: 'join' });
          console.log('[WebRTC] Joined room after receiving bound sessions');

          // Start periodic re-join in case the desktop reconnected before us
          // and doesn't know we're here yet
          startRejoinTimer();
        }
        break;

      case 'session_status':
        // A bound desktop session changed status (online/offline)
        console.log(`[WebRTC] Session ${message.sessionId} is now ${message.status} (connectionId: ${message.connectionId?.substring(0, 8) || 'none'})`);
        setBoundSessions(prev => {
          const updated = prev.map(session =>
            session.sessionId === message.sessionId
              ? { ...session, status: message.status, connectionId: message.connectionId, name: message.name || session.name }
              : session
          );
          boundSessionsRef.current = updated;
          return updated;
        });

        // When a paired desktop comes online, store its identity mapping
        // so we can validate incoming offers from this peer
        if (message.status === 'online' && message.connectionId && message.sessionId) {
          peerIdentitiesRef.current.set(message.connectionId, message.sessionId);
          // Show short session ID (strip 'desktop-' prefix for cleaner log)
          const shortSessionId = message.sessionId.replace(/^desktop-/, '').substring(0, 8);
          console.log(`[WebRTC] Stored identity mapping: ${message.connectionId.substring(0, 8)} → ${shortSessionId}`);
        }
        break;

      case 'pairing_success':
        // Successfully paired with a desktop
        console.log(`[WebRTC] Pairing successful: ${message.sessionName} (${message.sessionId})`);
        setPairingStatus({ success: true, sessionName: message.sessionName, sessionId: message.sessionId });
        // Add to bound sessions
        setBoundSessions(prev => {
          const updated = [
            ...prev.filter(s => s.sessionId !== message.sessionId),
            {
              sessionId: message.sessionId,
              name: message.sessionName,
              status: message.status || 'online',
              connectionId: message.desktopConnectionId,
              boundAt: Date.now(),
              role: normalizeRole(message.role)
            }
          ];
          boundSessionsRef.current = updated;
          return updated;
        });
        break;

      case 'pairing_failed':
        // Pairing failed
        console.log(`[WebRTC] Pairing failed: ${message.error}`);
        setPairingStatus({ success: false, error: message.error });
        break;

      // New monitor registration response
      case 'monitor_registered':
        console.log(`[WebRTC] Monitor registered: ${message.monitorId} (${message.name})`);
        break;

      // Pairing code generated (new flow - monitor generates codes)
      case 'pairing_code_created':
        console.log(`[WebRTC] Pairing code created: ${message.code}`);
        setGeneratedCode(message.code);
        setCodeExpiresAt(message.expiresAt);
        setCodeExpiresIn(message.expiresInSeconds);
        break;

      // Pairing code generation failed (e.g. user not authenticated). Without a
      // case here the error fell through to the default handler and the operator
      // saw nothing after clicking an empty gem slot.
      case 'pairing_code_error':
        console.error(`[WebRTC] Pairing code error: ${message.error}`);
        setGeneratedCode(null);
        setPairingError(message.error || 'Failed to create a pairing code');
        break;

      // Session was unpaired (desktop removed this monitor)
      case 'session_unpaired':
        console.log(`[WebRTC] Session unpaired: ${message.sessionId}`);
        setBoundSessions(prev => {
          const updated = prev.filter(s => s.sessionId !== message.sessionId);
          boundSessionsRef.current = updated;
          return updated;
        });
        break;

      // screen-share-started/stopped now handled via feature_message protocol (screen-capture feature)

      default:
        console.log('Unhandled message type:', message.type);
    }
  };

  const createPeerConnection = async (peerId) => {
    const peerConnection = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        ...(import.meta.env.VITE_TURN_URL ? [{
          urls: import.meta.env.VITE_TURN_URL,
          username: import.meta.env.VITE_TURN_USERNAME,
          credential: import.meta.env.VITE_TURN_CREDENTIAL
        }] : [])
      ]
    });

    peersRef.current.set(peerId, peerConnection);
    setPeers(new Map(peersRef.current));

    // Handle incoming data channels
    peerConnection.ondatachannel = (event) => {
      console.log(`[WebRTC] *** ondatachannel EVENT fired for peer: ${peerId.substring(0, 8)}`);
      const dataChannel = event.channel;
      console.log(`[WebRTC] Data channel received:`, {
        label: dataChannel.label,
        readyState: dataChannel.readyState,
        id: dataChannel.id
      });
      updatePeerNegotiation(peerId, {
        dataChannelState: dataChannel.readyState,
        timestamps: { dataChannelReceived: Date.now() },
      });
      peerDataChannelsRef.current.set(peerId, dataChannel);

      dataChannel.onopen = () => {
        console.log(`[WebRTC] ✓ Data channel OPENED for peer: ${peerId.substring(0, 8)}, readyState: ${dataChannel.readyState}`);
        updatePeerNegotiation(peerId, {
          dataChannelState: 'open',
          timestamps: { dataChannelOpen: Date.now() },
        });
        updateConnectedPeersList();
        // Flush any queued messages now that we have a DataChannel
        flushMessageQueue();
        // Start DataChannel heartbeat to detect dead peers
        startHeartbeat(peerId, dataChannel);
      };

      dataChannel.onclose = () => {
        // Verify the channel is actually closed before updating state
        // (onclose can fire prematurely in some WebRTC edge cases)
        if (dataChannel.readyState === 'closed') {
          console.log(`[WebRTC] Data channel CLOSED for peer: ${peerId.substring(0, 8)}`);
          stopHeartbeat(peerId);
          updatePeerNegotiation(peerId, {
            dataChannelState: 'closed',
            timestamps: { dataChannelClosed: Date.now() },
          });
          peerDataChannelsRef.current.delete(peerId);
          updateConnectedPeersList();
        } else {
          console.warn(`[WebRTC] onclose fired but readyState is ${dataChannel.readyState} for ${peerId.substring(0, 8)} - ignoring`);
        }
      };

      dataChannel.onerror = (event) => {
        // RTCErrorEvent.error has the diagnostic detail; the event itself
        // only carries {isTrusted: true}. Surface the inner fields so the
        // log is actually useful when the channel dies mid-conversation.
        const rtcError = event?.error || {};
        console.error(`[WebRTC] Data channel ERROR for peer: ${peerId.substring(0, 8)}`, {
          name: rtcError.name || null,
          errorDetail: rtcError.errorDetail || null,
          sctpCauseCode: rtcError.sctpCauseCode ?? null,
          message: rtcError.message || event?.message || 'unknown',
          channelLabel: dataChannel.label,
          channelState: dataChannel.readyState,
        });
      };

      dataChannel.onmessage = (e) => {
        // Record exact activity time on every message (cheap ref write, no
        // render), but only promote it to state at most once per second. This
        // keeps `lastDataReceived` accurate to ~1s for staleness/health UI
        // while avoiding a re-render of every useWebRTC() consumer on each
        // message (heartbeats and binary frames arrive before the early-returns
        // below, so they must still count as "data received").
        const now = Date.now();
        lastDataReceivedRef.current = now;
        if (dataReceivedFlushGateRef.current(now)) {
          setLastDataReceived(now);
        }
        try {
          // Check for binary data (ArrayBuffer) - used for video streaming
          if (e.data instanceof ArrayBuffer) {
            handleBinaryMessage(e.data, peerId);
            return;
          }

          let data = JSON.parse(e.data);

          // Handle heartbeat messages silently (no logging)
          if (data.type === 'dc_ping') {
            if (dataChannel.readyState === 'open') {
              dataChannel.send(JSON.stringify({ type: 'dc_pong', ts: data.ts }));
            }
            return;
          }
          if (data.type === 'dc_pong') {
            handlePong(peerId, data.ts);
            return;
          }

          // Handle chunked messages
          if (data.type === 'chunk') {
            const reassembled = handleIncomingChunk(data);
            if (!reassembled) {
              // Still waiting for more chunks
              return;
            }
            data = reassembled;
          }

          handleDataChannelMessage(data, peerId);
        } catch (error) {
          console.error('[WebRTC] Error parsing DataChannel message:', error);
        }
      };
    };

    // Handle remote tracks
    peerConnection.ontrack = (event) => {
      handleRemoteTrack(peerId, event.track, event.streams[0]);
    };

    // Handle ICE candidates - queue for batched sending
    peerConnection.onicecandidate = (event) => {
      // Check both that candidate exists AND has a non-empty candidate string
      // (empty string is end-of-candidates signal that should not be transmitted)
      if (event.candidate && event.candidate.candidate) {
        const c = event.candidate;
        console.log(`[WebRTC] ICE candidate: type=${c.type} protocol=${c.protocol} address=${c.address}:${c.port} relay=${c.relatedAddress || 'n/a'}`);
        queueIceCandidate(peerId, event.candidate);
      }
    };

    // Monitor connection state — clean up peers that disconnect or fail
    // Desktop will attempt ICE restart first; we give it time before tearing down.
    peerConnection.onconnectionstatechange = () => {
      const state = peerConnection.connectionState;
      console.log(`[WebRTC] Connection state with ${peerId.substring(0, 8)}: ${state}`);

      updatePeerNegotiation(peerId, {
        connectionState: state,
        timestamps: { [`connection_${state}`]: Date.now() },
      });

      const teardownPeer = () => {
        stopHeartbeat(peerId);
        peerConnection.close();
        peersRef.current.delete(peerId);
        peerDataChannelsRef.current.delete(peerId);
        pendingIceCandidatesRef.current.delete(peerId);
        videoTracksRef.current.delete(peerId);
        peerIdentitiesRef.current.delete(peerId);
        setPeers(new Map(peersRef.current));
        updateConnectedPeersList();
        if (peersRef.current.size === 0 && !intentionalDisconnectRef.current) {
          startRejoinTimer();
        }
      };

      if (state === 'closed') {
        console.log(`[WebRTC] Removing peer ${peerId.substring(0, 8)} (state: closed)`);
        teardownPeer();
      } else if (state === 'failed') {
        // Desktop will attempt ICE restart — wait for its restart offer before tearing down
        console.log(`[WebRTC] Connection failed with ${peerId.substring(0, 8)}, waiting for ICE restart from desktop...`);
        setTimeout(() => {
          const currentState = peerConnection.connectionState;
          if (currentState !== 'connected' && currentState !== 'connecting') {
            console.log(`[WebRTC] ICE restart did not recover ${peerId.substring(0, 8)} (state: ${currentState}), removing`);
            teardownPeer();
          }
        }, 20000);
      } else if (state === 'disconnected') {
        // Desktop will attempt ICE restart — give it time
        console.log(`[WebRTC] Connection disconnected with ${peerId.substring(0, 8)}, waiting for ICE restart...`);
        setTimeout(() => {
          const currentState = peerConnection.connectionState;
          if (currentState === 'disconnected' || currentState === 'failed') {
            console.log(`[WebRTC] Peer ${peerId.substring(0, 8)} did not recover from ${currentState}, removing`);
            teardownPeer();
          }
        }, 20000);
      }
    };

    peerConnection.oniceconnectionstatechange = () => {
      const iceState = peerConnection.iceConnectionState;
      console.log(`[WebRTC] ICE connection state with ${peerId.substring(0, 8)}: ${iceState}`);
      updatePeerNegotiation(peerId, {
        iceConnectionState: iceState,
        timestamps: { [`ice_${iceState}`]: Date.now() },
      });
    };

    peerConnection.onicegatheringstatechange = () => {
      updatePeerNegotiation(peerId, {
        iceGatheringState: peerConnection.iceGatheringState,
      });
    };

    return peerConnection
  };

  const handleOffer = async (peerId, offer) => {
    try {
      console.log(`[WebRTC] handleOffer from peer: ${peerId.substring(0, 8)}`);
      let peerConnection = peersRef.current.get(peerId);
      const isRenegotiation = !!peerConnection;

      if (!peerConnection) {
        console.log(`[WebRTC] Creating new peer connection for: ${peerId.substring(0, 8)}`);
        peerConnection = await createPeerConnection(peerId);
        console.log(`[WebRTC] Peer connection created, ondatachannel handler set`);
      } else {
        console.log(`[WebRTC] Renegotiation - reusing existing peer connection for: ${peerId.substring(0, 8)}`);
      }

      // Clear any stale ICE candidates from previous session before setting new remote description
      // This prevents "Unknown ufrag" errors when old ICE candidates don't match the new session
      if (pendingIceCandidatesRef.current.has(peerId)) {
        const staleCount = pendingIceCandidatesRef.current.get(peerId).length;
        if (staleCount > 0) {
          console.log(`[WebRTC] Clearing ${staleCount} stale ICE candidates for ${peerId.substring(0, 8)}`);
        }
        pendingIceCandidatesRef.current.delete(peerId);
      }

      console.log(`[WebRTC] Setting remote description (offer) from: ${peerId.substring(0, 8)}`);
      await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));

      // Process any queued ICE candidates now that remote description is set
      // (These would be candidates that arrived AFTER this offer but before setRemoteDescription completed)
      await processPendingIceCandidates(peerId);

      // Set the mic audio transceiver to sendonly BEFORE createAnswer() so the
      // answer SDP advertises that we will send audio. Without this, Chromium
      // defaults answerer transceivers to recvonly and no mic audio flows.
      const transceivers = peerConnection.getTransceivers();
      const audioTransceivers = transceivers.filter(t => t.receiver.track?.kind === 'audio');
      if (audioTransceivers.length >= 2) {
        const micTransceiver = audioTransceivers[audioTransceivers.length - 1];
        micTransceiver.direction = 'sendonly';
        console.log(`[WebRTC] Set mic transceiver mid=${micTransceiver.mid} direction to sendonly`);
      }

      const answer = await peerConnection.createAnswer();

      // Apply SDP munging to prefer H.264 for hardware encoding on desktop
      const modifiedSdp = preferH264(answer.sdp);
      await peerConnection.setLocalDescription({ ...answer, sdp: modifiedSdp });

      console.log(`[WebRTC] Sending answer to: ${peerId.substring(0, 8)} (WS state: ${wsRef.current?.readyState})`);
      sendMessage({
        type: 'answer',
        targetId: peerId,
        data: { ...answer, sdp: modifiedSdp }
      });
      console.log(`[WebRTC] Answer sent successfully to: ${peerId.substring(0, 8)}`);
      updatePeerNegotiation(peerId, {
        answerSent: true,
        timestamps: { answerSent: Date.now() },
      });
    } catch (error) {
      console.error(`[WebRTC] Error in handleOffer for ${peerId.substring(0, 8)}:`, error);
      updatePeerNegotiation(peerId, {
        error: error.message,
        timestamps: { error: Date.now() },
      });
    }
  };

  /**
   * Prefer H.264 codec in SDP for hardware encoding
   * Reorders codecs: H.264 > VP9 > VP8
   */
  const preferH264 = (sdp) => {
    if (!sdp) return sdp;

    const lines = sdp.split('\r\n');

    // Find ALL video m= lines
    const videoMLineIndices = [];
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].startsWith('m=video')) {
        videoMLineIndices.push(i);
      }
    }

    if (videoMLineIndices.length === 0) {
      return sdp;
    }

    // Process each video m= line
    for (const videoMLineIndex of videoMLineIndices) {
      const videoMLine = lines[videoMLineIndex];
      const mLineParts = videoMLine.split(' ');
      const originalPayloadTypes = mLineParts.slice(3);

      const h264PayloadTypes = [];
      const vp9PayloadTypes = [];
      const vp8PayloadTypes = [];
      const otherPayloadTypes = [];

      for (const pt of originalPayloadTypes) {
        const rtpmapLine = lines.find(l => l.startsWith(`a=rtpmap:${pt} `));
        if (rtpmapLine) {
          const codecMatch = rtpmapLine.match(/a=rtpmap:\d+ ([^/]+)/);
          const codec = codecMatch ? codecMatch[1] : 'unknown';

          if (codec.toLowerCase() === 'h264') {
            h264PayloadTypes.push(pt);
          } else if (codec.toLowerCase() === 'vp9') {
            vp9PayloadTypes.push(pt);
          } else if (codec.toLowerCase() === 'vp8') {
            vp8PayloadTypes.push(pt);
          } else {
            otherPayloadTypes.push(pt);
          }
        } else {
          otherPayloadTypes.push(pt);
        }
      }

      if (h264PayloadTypes.length === 0 && vp9PayloadTypes.length === 0) {
        continue;
      }

      // Prefer H.264 > VP9 > others > VP8
      const newPayloadOrder = [
        ...h264PayloadTypes,
        ...vp9PayloadTypes,
        ...otherPayloadTypes,
        ...vp8PayloadTypes
      ];

      const newMLine = mLineParts.slice(0, 3).concat(newPayloadOrder).join(' ');
      lines[videoMLineIndex] = newMLine;
    }

    return lines.join('\r\n');
  };

  const handleAnswer = async (peerId, answer) => {
    const peerConnection = peersRef.current.get(peerId);
    if (peerConnection) {
      await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
    }
  };

  const handleIceCandidate = async (peerId, candidate) => {
    // Skip null/empty candidates (end-of-candidates signal)
    // WebRTC sends these when ICE gathering completes - they should not be added
    if (!candidate || !candidate.candidate) {
      console.log(`[WebRTC] Ignoring end-of-candidates signal from ${peerId.substring(0, 8)}`);
      return;
    }

    const peerConnection = peersRef.current.get(peerId);

    // Queue ICE candidate if:
    // 1. No peer connection exists yet (offer hasn't arrived), OR
    // 2. Peer connection exists but remote description not set yet
    if (!peerConnection || !peerConnection.remoteDescription) {
      const reason = !peerConnection ? 'no peer connection yet' : 'remote description not set';
      console.log(`[WebRTC] Queueing ICE candidate from ${peerId.substring(0, 8)} - ${reason}`);
      if (!pendingIceCandidatesRef.current.has(peerId)) {
        pendingIceCandidatesRef.current.set(peerId, []);
      }
      pendingIceCandidatesRef.current.get(peerId).push(candidate);
      return;
    }

    // Add ICE candidate immediately if remote description is set
    try {
      await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (error) {
      // "Unknown ufrag" errors are expected during session transitions (renegotiation)
      // These occur when ICE candidates from a previous session arrive after a new offer
      if (error.message?.includes('Unknown ufrag')) {
        console.log(`[WebRTC] Ignoring stale ICE candidate for ${peerId.substring(0, 8)} (session changed)`);
      } else {
        console.error(`[WebRTC] Error adding ICE candidate for ${peerId.substring(0, 8)}:`, error);
      }
    }
  };

  const processPendingIceCandidates = async (peerId) => {
    const peerConnection = peersRef.current.get(peerId);
    const candidates = pendingIceCandidatesRef.current.get(peerId);

    if (peerConnection && candidates && candidates.length > 0) {
      console.log(`[WebRTC] Processing ${candidates.length} queued ICE candidates for ${peerId.substring(0, 8)}`);

      let added = 0;
      let stale = 0;
      for (const candidate of candidates) {
        try {
          await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
          added++;
        } catch (error) {
          // "Unknown ufrag" means the candidate is from a previous session - expected during transitions
          if (error.message?.includes('Unknown ufrag')) {
            stale++;
          } else {
            console.error(`[WebRTC] Error adding queued ICE candidate for ${peerId.substring(0, 8)}:`, error);
          }
        }
      }

      if (stale > 0) {
        console.log(`[WebRTC] ICE candidates for ${peerId.substring(0, 8)}: ${added} added, ${stale} stale (ignored)`);
      }

      // Clear the queue
      pendingIceCandidatesRef.current.delete(peerId);
    }
  };

  /**
   * Handle incoming binary data from DataChannel (video streaming)
   * Binary format: Header (12 bytes) + Chunk data
   * Header: streamIdHash (4 bytes) + chunkIndex (4 bytes) + flags (4 bytes)
   */
  const handleBinaryMessage = (data, peerId) => {
    if (data.byteLength < 12) {
      console.warn('[WebRTC] Binary message too small, ignoring');
      return;
    }

    // Parse header
    const view = new DataView(data);
    const streamIdHash = view.getUint32(0, true);
    const chunkIndex = view.getUint32(4, true);
    const flags = view.getUint32(8, true);
    const isLast = (flags & 1) !== 0;

    // Get chunk data (everything after header)
    const chunkData = data.slice(12);

    // Route to registered callback for this stream
    const callback = binaryCallbacksRef.current.get(streamIdHash);
    if (callback) {
      try {
        callback(chunkData, chunkIndex, isLast, peerId);
      } catch (error) {
        console.error('[WebRTC] Error in binary callback:', error);
      }
    } else {
      console.warn(`[WebRTC] No callback registered for stream hash: ${streamIdHash}`);
    }
  };

  /**
   * Register a callback for binary data from a specific stream
   * @param {number} streamIdHash - Hash of the stream ID
   * @param {Function} callback - Function(chunkData, chunkIndex, isLast, peerId)
   * @returns {Function} Unsubscribe function
   */
  const registerBinaryCallback = (streamIdHash, callback) => {
    binaryCallbacksRef.current.set(streamIdHash, callback);
    console.log(`[WebRTC] Registered binary callback for stream hash: ${streamIdHash}`);
    return () => {
      binaryCallbacksRef.current.delete(streamIdHash);
      console.log(`[WebRTC] Unregistered binary callback for stream hash: ${streamIdHash}`);
    };
  };

  /**
   * Clear all binary callbacks (called on cleanup)
   */
  const clearBinaryCallbacks = () => {
    binaryCallbacksRef.current.clear();
  };

  const handleDataChannelMessage = async (data, peerId) => {
    data.fromId = data.fromId || peerId;

    if (data.type === 'capabilities') {
      featureManagerRef.current.setRemoteCapabilities(data.capabilities);
      setRemoteCapabilities(data.capabilities);
      if (data.screenWidth && data.screenHeight) {
        console.log(`[WebRTC] Received native screen resolution: ${data.screenWidth}x${data.screenHeight}`);
        coordinateMapper.setNativeResolution(data.screenWidth, data.screenHeight);
      }
      return;
    }

    if (data.type === 'feature_message') {
      await featureManagerRef.current.routeMessage(data);
      return;
    }

    // Fallthrough for any unknown types
    console.warn('[WebRTC] Unknown DataChannel message type:', data.type);
  };

  const handleRemoteTrack = (peerId, track, stream) => {
    if (track.kind === 'audio') {
      console.log(`[WebRTC] Ignoring remote audio track from ${peerId.substring(0, 8)} (desktop doesn't send audio)`);
      return;
    }
    if (track.kind !== 'video') return;

    // Get or create track storage for this peer
    if (!videoTracksRef.current.has(peerId)) {
      videoTracksRef.current.set(peerId, { camera: null, screenShare: null, screenShareActive: false });
    }
    const peerTracks = videoTracksRef.current.get(peerId);

    // Determine stream type based on what we've already stored
    const streamType = peerTracks.camera === null ? 'Camera' : 'Screen-Share';

    // Store the track
    if (streamType === 'Camera') {
      peerTracks.camera = track;
    } else {
      peerTracks.screenShare = track;
    }

    console.log(`[WebRTC] Stored ${streamType} track for peer ${peerId.substring(0, 8)}`, {
      streamType,
      readyState: track.readyState,
      muted: track.muted,
      screenShareActive: peerTracks.screenShareActive
    });

    // Attach camera video immediately, but defer screen share until
    // 'screen-share-started' arrives (the screen share transceiver is a
    // placeholder that starts with no frames — attaching it now would
    // just show a black video element).
    if (streamType === 'Camera') {
      // If screenShareActive was already set (screen_share_started arrived first),
      // attach this track as the screen share instead of camera
      if (peerTracks.screenShareActive) {
        console.log(`[WebRTC] screenShareActive already set for ${peerId.substring(0, 8)}, attaching camera track as Screen-Share`);
        attachVideoToContainer(peerId, 'Screen-Share', track);
      } else {
        attachVideoToContainer(peerId, streamType, track);
      }
    } else if (streamType === 'Screen-Share' && peerTracks.screenShareActive) {
      // Screen share track arrived after screen_share_started — attach immediately
      console.log(`[WebRTC] Attaching late-arriving Screen-Share track for ${peerId.substring(0, 8)}`);
      attachVideoToContainer(peerId, 'Screen-Share', track);
    }
  };

  /**
   * Create and attach a video element to the appropriate container
   */
  const attachVideoToContainer = (peerId, streamType, track) => {
    if (track.readyState === 'ended') {
      console.log(`[WebRTC] Skipping ended ${streamType} track for ${peerId.substring(0, 8)}`);
      return false;
    }

    const screenShareContainer = document.getElementById('screenShareContainer');
    const cameraPreviewContainer = document.getElementById('cameraPreviewContainer');

    const targetContainer = streamType === 'Camera' ? cameraPreviewContainer : screenShareContainer;

    if (!targetContainer) {
      console.log(`[WebRTC] Container for ${streamType} not found, video will be attached when component mounts`);
      return false;
    }

    const videoId = `video-${peerId}-${streamType}`;
    let videoWrapper = document.getElementById(videoId);

    if (!videoWrapper) {
      // Remove any existing video elements of the same type from OTHER peers
      // This handles the case where backend restarts with a new peerId
      const existingElements = document.querySelectorAll(`[data-type="${streamType}"]`);
      existingElements.forEach(element => {
        const elementPeerId = element.getAttribute('data-peer');
        if (elementPeerId !== peerId) {
          console.log(`[WebRTC] Removing old ${streamType} element from peer ${elementPeerId?.substring(0, 8)}`);
          element.remove();
          // Clean up stored tracks for the old peer
          if (elementPeerId && videoTracksRef.current.has(elementPeerId)) {
            const oldTracks = videoTracksRef.current.get(elementPeerId);
            if (streamType === 'Camera') {
              oldTracks.camera = null;
            } else {
              oldTracks.screenShare = null;
            }
          }
        }
      });

      console.log(`[WebRTC] Creating video element for ${streamType} from peer ${peerId}`);

      videoWrapper = document.createElement('div');
      videoWrapper.className = streamType === 'Camera' ? 'camera-preview' : 'video-wrapper';
      videoWrapper.id = videoId;
      videoWrapper.setAttribute('data-peer', peerId);
      videoWrapper.setAttribute('data-type', streamType);

      const title = document.createElement('h3');
      title.textContent = `${peerId.substring(0, 8)} - ${streamType}`;

      const video = document.createElement('video');
      video.autoplay = true;
      video.playsInline = true;
      video.muted = false; // Allow audio
      video.srcObject = new MediaStream([track]);

      // Add loaded event to confirm video is ready
      video.onloadedmetadata = () => {
        console.log(`[WebRTC] Video metadata loaded for ${streamType}`, {
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight
        });
        video.play().catch(err => console.error('[WebRTC] Error playing video:', err));
      };

      // Add click handler for screen share
      if (streamType === 'Screen-Share') {
        const windowControlFeature = featureManagerRef.current.getFeature('window-control');
        if (windowControlFeature) {
          windowControlFeature.setupScreenShareClickHandler(video, peerId);
        }
      }

      videoWrapper.appendChild(title);
      videoWrapper.appendChild(video);
      targetContainer.appendChild(videoWrapper);

      console.log(`[WebRTC] ${streamType} video added to container`);
    }

    return true;
  };

  /**
   * Re-attach all stored video tracks to containers
   * Called when video container components mount.
   * useCallback so consumers using this as a useEffect dep don't loop on every
   * provider re-render (which happens constantly from setLastDataReceived etc).
   */
  const attachAllVideos = useCallback(() => {
    console.log('[WebRTC] Re-attaching all video tracks...');
    let attached = 0;

    videoTracksRef.current.forEach((tracks, peerId) => {
      if (tracks.screenShare) {
        if (attachVideoToContainer(peerId, 'Screen-Share', tracks.screenShare)) {
          attached++;
        }
      } else if (tracks.screenShareActive && tracks.camera) {
        // Fallback: when desktop sends no camera (video:false), the screen share
        // placeholder is the only video track and gets stored as 'camera'.
        // Use it for screen share when we know screen sharing is active.
        console.log(`[WebRTC] Using camera track as screen share fallback for ${peerId.substring(0, 8)}`);
        if (attachVideoToContainer(peerId, 'Screen-Share', tracks.camera)) {
          attached++;
        }
      }
      if (tracks.camera) {
        if (attachVideoToContainer(peerId, 'Camera', tracks.camera)) {
          attached++;
        }
      }
    });

    console.log(`[WebRTC] Re-attached ${attached} video(s)`);
    return attached;
  }, []);

  /**
   * Get diagnostic info about video tracks for debugging "waiting for stream" issues
   */
  const getVideoTrackDiagnostics = useCallback(() => {
    const diag = [];
    videoTracksRef.current.forEach((tracks, peerId) => {
      const id = peerId.substring(0, 8);
      const cam = tracks.camera;
      const ss = tracks.screenShare;
      diag.push({
        peer: id,
        camera: cam ? `${cam.readyState}${cam.muted ? '/muted' : ''}` : 'none',
        screenShare: ss ? `${ss.readyState}${ss.muted ? '/muted' : ''}` : 'none',
        screenShareActive: tracks.screenShareActive
      });
    });
    return diag;
  }, []);

  // Feature activation methods
  // Wrapped in useCallback so consumers' `useEffect([... activateFeatures])`
  // doesn't fire on every WebRTCContext re-render. Without this, useViewFeatures
  // re-fires its activate/deactivate cycle dozens of times per second whenever
  // any WebRTCContext state changes — drowning real feature requests in noise.
  const activateFeatures = useCallback(async (featureIds) => {
    if (featureManagerRef.current) {
      await featureManagerRef.current.activateFeatures(featureIds);
    }
  }, []);

  const deactivateFeatures = useCallback(async (featureIds) => {
    if (featureManagerRef.current) {
      await featureManagerRef.current.deactivateFeatures(featureIds);
    }
  }, []);

  // Monitor name management
  const updateMonitorName = (name) => {
    setMonitorName(name);
    localStorage.setItem('monitorName', name);
    // Re-register with new name if connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      sendMessage({ type: 'register_monitor', monitorId, name: name || 'Web Monitor' });
    }
  };

  // Generate a pairing code (new flow - monitor generates codes for desktops to enter).
  // The optional role is the capability tier the resulting pairing will have;
  // it is baked into the code at generation time and cannot be changed later.
  const generatePairingCode = (role = DEFAULT_ROLE) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('[WebRTC] Cannot generate pairing code: not connected');
      return;
    }
    const normalizedRole = normalizeRole(role);
    console.log(`[WebRTC] Requesting pairing code generation (role=${normalizedRole})`);
    setGeneratedCode(null); // Clear previous code
    setPairingError(null);  // Clear any stale failure
    sendMessage({ type: 'create_pairing_code', monitorId, role: normalizedRole });
  };

  // Acknowledge/clear the last pairing error once surfaced to the operator.
  const clearPairingError = () => setPairingError(null);

  // Clear generated code
  const clearGeneratedCode = () => {
    setGeneratedCode(null);
    setCodeExpiresAt(null);
    setCodeExpiresIn(null);
  };

  // Session management functions (legacy - for backward compatibility)
  const usePairingCode = (code) => {
    // DEPRECATED: Old flow where viewer entered codes
    // Now desktops enter codes, not viewers
    console.warn('[WebRTC] usePairingCode is deprecated - use generatePairingCode instead');
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('[WebRTC] Cannot use pairing code: not connected');
      setPairingStatus({ success: false, error: 'Not connected' });
      return;
    }
    console.log(`[WebRTC] Using pairing code: ${code}`);
    setPairingStatus(null); // Clear previous status
    sendMessage({ type: 'use_pairing_code', code, viewerId });
  };

  const refreshBoundSessions = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('[WebRTC] Cannot refresh sessions: not connected');
      return;
    }
    sendMessage({ type: 'get_bound_sessions', viewerId: monitorId });
  };

  const unbindSession = (sessionId) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('[WebRTC] Cannot unbind session: not connected');
      return;
    }
    sendMessage({ type: 'unbind_session', viewerId: monitorId, sessionId });
    // Optimistically remove from local state
    setBoundSessions(prev => {
      const updated = prev.filter(s => s.sessionId !== sessionId);
      boundSessionsRef.current = updated;
      return updated;
    });
  };

  const clearPairingStatus = () => {
    setPairingStatus(null);
  };

  // Clear auth error
  const clearAuthError = () => setAuthError(null);

  // =========================================================================
  // Live Microphone — stream audio from website to desktop
  // =========================================================================
  const [isMicActive, setIsMicActive] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const micStreamRef = useRef(null);
  const micTrackRef = useRef(null);
  /**
   * Start streaming microphone audio to all connected peers.
   * Finds the audio transceiver (created by desktop's recvonly) and replaces
   * the sender track with the mic track — no renegotiation needed.
   */
  const startMicrophone = useCallback(async () => {
    if (micStreamRef.current) {
      console.log('[WebRTC] Mic already active');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioTrack = stream.getAudioTracks()[0];

      micStreamRef.current = stream;
      micTrackRef.current = audioTrack;

      // Attach mic track to the LAST audio transceiver — that's the one created by
      // desktop's addTransceiver('audio', { direction: 'recvonly' }), which appears after
      // the sendrecv transceiver from desktop's addTrack(micTrack).
      // Chromium defaults all answerer transceivers to recvonly, so we must explicitly
      // set direction to 'sendonly' before media will flow.
      peersRef.current.forEach((pc, peerId) => {
        const transceivers = pc.getTransceivers();
        console.log(`[WebRTC] All transceivers for ${peerId.substring(0, 8)}:`, transceivers.map((t, i) =>
          `[${i}] kind=${t.receiver.track?.kind} dir=${t.direction} curDir=${t.currentDirection} senderTrack=${t.sender.track?.kind || 'null'} mid=${t.mid}`
        ).join(' | '));

        // Find the last audio transceiver — that's the desktop's recvonly mic slot
        const audioTransceivers = transceivers.filter(t => t.receiver.track?.kind === 'audio');
        const audioTransceiver = audioTransceivers[audioTransceivers.length - 1];

        if (audioTransceiver) {
          console.log(`[WebRTC] Using audio transceiver mid=${audioTransceiver.mid} (dir=${audioTransceiver.direction}, curDir=${audioTransceiver.currentDirection})`);

          // Ensure direction allows sending — if still recvonly from initial
          // negotiation, change to sendonly so audio actually flows.
          if (audioTransceiver.direction === 'recvonly' || audioTransceiver.direction === 'inactive') {
            audioTransceiver.direction = 'sendonly';
            console.log(`[WebRTC] Changed transceiver direction to sendonly`);
          }

          audioTransceiver.sender.replaceTrack(audioTrack).then(() => {
            console.log(`[WebRTC] Mic track attached to peer ${peerId.substring(0, 8)} (mid=${audioTransceiver.mid}, dir=${audioTransceiver.direction})`);
          }).catch(err => {
            console.error(`[WebRTC] Failed to attach mic to ${peerId.substring(0, 8)}:`, err);
          });
        } else {
          console.warn(`[WebRTC] No audio transceiver found for ${peerId.substring(0, 8)}`);
        }
      });

      setIsMicActive(true);
      setIsMicMuted(false);
      console.log('[WebRTC] Microphone started');

      // Notify desktop via feature message
      sendFeatureMessage({
        feature: 'ai-voice',
        version: 'v2.0',
        cmd: 'mic_stream_started',
        payload: {}
      });
    } catch (err) {
      console.error('[WebRTC] Failed to start microphone:', err);
      throw err;
    }
  }, []);

  /**
   * Stop streaming microphone audio.
   */
  const stopMicrophone = useCallback(() => {
    if (!micStreamRef.current) return;

    // Stop all tracks
    micStreamRef.current.getTracks().forEach(track => track.stop());

    // Remove track from the mic audio transceiver (last audio transceiver)
    peersRef.current.forEach((pc, peerId) => {
      const audioTransceivers = pc.getTransceivers().filter(t => t.receiver.track?.kind === 'audio');
      const audioTransceiver = audioTransceivers[audioTransceivers.length - 1];
      if (audioTransceiver) {
        audioTransceiver.sender.replaceTrack(null).catch(err => console.warn('[WebRTC] Failed to remove mic track:', err.message));
      }
    });

    micStreamRef.current = null;
    micTrackRef.current = null;
    setIsMicActive(false);
    setIsMicMuted(false);
    console.log('[WebRTC] Microphone stopped');

    // Notify desktop
    sendFeatureMessage({
      feature: 'ai-voice',
      version: 'v2.0',
      cmd: 'mic_stream_stopped',
      payload: {}
    });
  }, []);

  /**
   * Toggle microphone mute (keeps stream alive, just disables the track).
   */
  const toggleMicMute = useCallback(() => {
    if (!micTrackRef.current) return;
    const newMuted = !micTrackRef.current.enabled;
    micTrackRef.current.enabled = newMuted;
    setIsMicMuted(!newMuted);
    // Note: enabled=true means NOT muted, enabled=false means muted
    // So we flip: isMicMuted = !track.enabled
    setIsMicMuted(!micTrackRef.current.enabled);
  }, []);

  const connectionState = useMemo(() => {
    if (!isConnected) return 'disconnected';
    if (connectionStats?.quality === 'disconnected') return 'reconnecting';
    return 'connected';
  }, [isConnected, connectionStats?.quality]);

  // Memoized so a provider re-render that changes nothing in `value` doesn't
  // hand consumers a new object (context re-renders every consumer on identity
  // change). Deps list every reactive field below. Note: several actions
  // (connect, disconnect, the pairing/queue callbacks) are still re-created each
  // render because they close over reactive state / the large signaling graph;
  // they are listed as deps so the value never serves a stale action. The
  // guaranteed per-message win comes from throttling `lastDataReceived` above.
  const value = useMemo(() => ({
    // State
    wsUrl,
    setWsUrl,
    isConnected,
    status,
    peers,
    connectedPeers,
    authError, // Authentication error (e.g., token expired)
    clearAuthError, // Clear auth error

    // Monitor identity state
    monitorId,
    monitorName,
    updateMonitorName,
    viewerId, // Alias for backward compatibility
    sessionId, // Current WebSocket session ID

    // Pairing code state (new flow - monitor generates codes)
    generatedCode,
    codeExpiresAt,
    codeExpiresIn,
    pairingError,
    clearPairingError,

    // Session state
    boundSessions,
    pairingStatus,

    // Message queue state (for when DataChannel not available)
    messageQueue,
    messageQueueLength: messageQueue.length,

    // Actions
    connect,
    disconnect,
    validateConnections, // Request backend to validate and clean up stale connections
    attachAllVideos, // Re-attach videos when components mount
    activateFeatures, // Activate features when view mounts
    deactivateFeatures, // Deactivate features when view unmounts
    flushMessageQueue, // Manually flush queued messages

    // Session actions (new flow)
    generatePairingCode, // Generate a pairing code for desktops to enter
    clearGeneratedCode, // Clear the generated pairing code
    refreshBoundSessions, // Refresh the list of bound sessions
    unbindSession, // Remove binding to a desktop session
    clearPairingStatus, // Clear pairing status message

    // Legacy actions (deprecated)
    usePairingCode, // DEPRECATED: Old flow where viewer entered codes

    // Selected peer management (for SocketContext integration)
    registerSelectedPeerIdGetter, // Register getter function from SocketContext
    getSelectedPeerId, // Get current selected peer ID

    // Binary streaming support
    registerBinaryCallback, // Register callback for binary video chunks

    // Feature messaging
    sendFeatureMessage, // Send a feature message via DataChannel

    // Connection status helpers
    hasActiveDataChannel, // Check if a peer has an active DataChannel (checks ref directly)

    // Connection quality
    connectionStats, // { latency, quality, lastPingAt } from heartbeat RTT
    connectionState, // 'connected' | 'reconnecting' | 'disconnected'
    lastDataReceived, // Timestamp of last DataChannel message
    reconnectAttempt, // Current reconnect attempt number

    // Remote capabilities (updated on connect and on settings change)
    remoteCapabilities,

    // Connection debug info
    getVideoTrackDiagnostics, // Get diagnostic info about stored video tracks
    peerNegotiationInfo, // Map of peerId -> negotiation state for debugging
    hasJoined: hasJoinedRef.current,
    boundSessionsLoaded: boundSessions.length > 0 || hasJoinedRef.current,

    // Live Microphone
    isMicActive,
    isMicMuted,
    startMicrophone,
    stopMicrophone,
    toggleMicMute,

    // Refs (non-reactive: read at recompute time. Intentionally NOT in the dep
    // array — ref mutations never trigger a render, and in practice they change
    // alongside a state dep that does, so the memo still picks up fresh values.)
    featureManager: featureManagerRef.current
  }), [
    wsUrl, setWsUrl, isConnected, status, peers, connectedPeers, authError, clearAuthError,
    monitorId, monitorName, updateMonitorName, viewerId, sessionId,
    generatedCode, codeExpiresAt, codeExpiresIn, pairingError, clearPairingError,
    boundSessions, pairingStatus, messageQueue,
    connect, disconnect, validateConnections, attachAllVideos, activateFeatures, deactivateFeatures,
    flushMessageQueue, generatePairingCode, clearGeneratedCode, refreshBoundSessions, unbindSession,
    clearPairingStatus, usePairingCode, registerSelectedPeerIdGetter, getSelectedPeerId,
    registerBinaryCallback, sendFeatureMessage, hasActiveDataChannel,
    connectionStats, connectionState, lastDataReceived, reconnectAttempt, remoteCapabilities,
    getVideoTrackDiagnostics, peerNegotiationInfo,
    isMicActive, isMicMuted, startMicrophone, stopMicrophone, toggleMicMute,
  ]);

  return (
    <WebRTCContext.Provider value={value}>
      {children}
    </WebRTCContext.Provider>
  );
};
